import { d, u, _, x, k } from "./preact-core.js";
import { h as handleize } from "./utilities.js";
import { s as setMoney } from "./layout-store.js";
import { C as CustomSelect } from "./custom-select.js";
function luma(r, g, b) {
  let red = 0;
  let green = 0;
  let blue = 0;
  if (typeof r === "string") {
    const colorString = r;
    let match;
    const hexRegex = /^\#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/;
    if (match = colorString.match(hexRegex)) {
      let hex = match[1];
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      const rgb = parseInt(hex, 16);
      red = rgb >> 16 & 255;
      green = rgb >> 8 & 255;
      blue = rgb >> 0 & 255;
    } else {
      const rgbRegex = /^rgb\(([0-9]{1,3}),[ +]?([0-9]{1,3}),[ +]?([0-9]{1,3})\)$/;
      const rgbaRegex = /^rgba\(([0-9]{1,3}),[ +]?([0-9]{1,3}),[ +]?([0-9]{1,3}),[ +]?([0-9]{1}|(?:[0-9]{1})?\.[0-9]+)\)$/;
      if ((match = colorString.match(rgbRegex)) || (match = colorString.match(rgbaRegex))) {
        red = parseFloat(match[1]);
        green = parseFloat(match[2]);
        blue = parseFloat(match[3]);
      }
    }
  } else {
    red = parseFloat(String(r)) || 0;
    green = parseFloat(String(g)) || 0;
    blue = parseFloat(String(b)) || 0;
  }
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}
class ProductFormUtils {
  constructor() {
    this.subscribeDiscount = d(null);
    this.currencyIso = window.currencyIso ? window.currencyIso : "";
    this.currencySymbol = window.currencySymbol ? window.currencySymbol : "$";
  }
  getSubscriptionPriceStr(price) {
    if (!this.subscribeDiscount.value) return price;
    const { value, value_type } = this.subscribeDiscount.value;
    return value_type === "percentage" ? price - price * value / 100 : price - value;
  }
  getSplitVariantPriceStr(qtyVar, optionValue, split, showCurrencyIso = true) {
    const parsedValue = parseFloat(optionValue.split(" ")[0]);
    const isValidNumber = !isNaN(parsedValue);
    const adjustedPrice = isValidNumber ? qtyVar.price / parsedValue : qtyVar.price;
    if (!split) {
      return setMoney(qtyVar.price);
    }
    const priceStr = `${setMoney(this.getSubscriptionPriceStr(adjustedPrice))} ${showCurrencyIso ? ` ${this.currencyIso}` : ""}`;
    return isValidNumber && split ? `${priceStr} each` : priceStr;
  }
  getSplitVariantPrice(options) {
    const {
      optionIndex,
      optionValue,
      selectedOptions,
      variantData,
      split,
      showCurrencyIso = true
      // Default to true
    } = options;
    if (selectedOptions.length === 0) return "";
    const qtyVars = variantData.filter(
      (variant) => variant.options[optionIndex] === optionValue
    );
    if (!qtyVars.length) return "";
    if (qtyVars.every((qtyVar) => qtyVar.price === qtyVars[0].price)) {
      return this.getSplitVariantPriceStr(
        qtyVars[0],
        optionValue,
        split,
        showCurrencyIso
      );
    }
    const currentOptionsWithSelected = [
      ...selectedOptions.slice(0, optionIndex),
      optionValue,
      ...selectedOptions.slice(optionIndex + 1)
    ];
    const matchingVariant = qtyVars.find((variant) => {
      const variantOptionsWithSelected = [
        ...variant.options.slice(0, optionIndex),
        optionValue,
        ...variant.options.slice(optionIndex + 1)
      ];
      return variantOptionsWithSelected.every(
        (option, idx) => currentOptionsWithSelected[idx] === option
      );
    });
    return matchingVariant ? this.getSplitVariantPriceStr(
      matchingVariant,
      optionValue,
      split,
      showCurrencyIso
    ) : "";
  }
  // Price String Functions
  getQtyPriceStr(options = {}) {
    const {
      qtyVar,
      split = true,
      optionLabel = "",
      showCurrencyIso = true
      // Default to true
    } = options;
    if (!qtyVar) return "";
    const labelNumber = parseFloat(qtyVar.title.split(" ")[0]);
    const lowerCaseLabel = optionLabel.toLowerCase();
    const isQtyOrSizeOption = ["qty", "quantity", "size"].some(
      (term) => lowerCaseLabel.includes(term)
    );
    const shouldShowUnitPrice = !isNaN(labelNumber) && isQtyOrSizeOption && split;
    const adjustedPrice = shouldShowUnitPrice ? qtyVar.price / labelNumber : qtyVar.price;
    if (setMoney(this.getSubscriptionPriceStr(adjustedPrice)) === qtyVar.title) {
      return "";
    }
    const priceStr = `- ${setMoney(this.getSubscriptionPriceStr(adjustedPrice))} ${showCurrencyIso ? `${this.currencyIso}` : ""}`;
    return shouldShowUnitPrice && setMoney(this.getSubscriptionPriceStr(adjustedPrice)) != qtyVar.title ? `${priceStr} each` : priceStr;
  }
  fixInitialOptionsFormat(options) {
    if (options.length === 1) {
      const option = options[0];
      return "value" in option ? option.value : option;
    }
    return options.map((option) => "value" in option ? option.value : option);
  }
  /**
   * Renders the appropriate price string based on product options
   * @param options - Configuration object for price rendering
   * @returns Formatted price string or null if no valid price can be determined
   */
  renderPriceString(options) {
    const {
      singleOptionSet,
      value,
      optionLabel,
      currentOptionIndex,
      selectedOptions,
      variantData,
      split,
      showCurrencyIso = true
      // Default to true
    } = options;
    if (singleOptionSet) {
      return this.getQtyPriceStr({
        qtyVar: value,
        optionLabel,
        split,
        showCurrencyIso
      });
    }
    if (isNaN(parseFloat(value.label.split(" ")[0]))) {
      return null;
    }
    return this.getSplitVariantPrice({
      optionValue: value.label,
      optionIndex: currentOptionIndex,
      selectedOptions,
      variantData,
      split,
      showCurrencyIso
    });
  }
}
const productFormUtils = new ProductFormUtils();
const VariantButtonOption = ({
  option,
  dropdownOptions,
  defaultOption,
  currentOptionIndex,
  singleOptionSet,
  selectedOptions,
  selectedVariant,
  variantData,
  productOptions,
  showOptionLabel,
  showVariantPrice,
  onSelect
}) => {
  let optionLabel = option.name;
  let defaultOptionSet = dropdownOptions;
  if (singleOptionSet) {
    optionLabel = productOptions[0].name;
    defaultOptionSet = variantData;
  }
  const buttonType = "--pill";
  const renderOptionColor = (value) => {
    return value.color ? /* @__PURE__ */ u(
      "span",
      {
        class: `variant__color ${value.color && luma(value.color) > 190 ? "light" : ""}`,
        style: { background: value.color }
      }
    ) : null;
  };
  const renderPriceString = (options) => {
    return productFormUtils.renderPriceString({
      ...options,
      selectedOptions,
      variantData
    });
  };
  const isValueSelected = (value, index) => {
    const currentSelection = selectedOptions[index];
    return value.title === currentSelection || value.value === currentSelection || value.label === currentSelection;
  };
  return /* @__PURE__ */ u("div", { class: "variant-wrap variant-wrap--buttons --row", children: [
    showOptionLabel ? /* @__PURE__ */ u("span", { class: "variant-heading", children: optionLabel }) : null,
    /* @__PURE__ */ u(
      "div",
      {
        class: `variant__radios variant__radios--${handleize(optionLabel)} variant-radios--${defaultOptionSet.length} ${buttonType}`,
        children: defaultOptionSet.map((value) => /* @__PURE__ */ u("div", { class: "variant__radio", children: /* @__PURE__ */ u(
          "button",
          {
            type: "button",
            class: `${isValueSelected(value, currentOptionIndex) ? "active" : ""} ${buttonType}`,
            onClick: () => onSelect(currentOptionIndex, value, singleOptionSet),
            "aria-label": `${optionLabel} ${value.label || value.title}`,
            children: /* @__PURE__ */ u("span", { class: "variant__radio-wrap", children: [
              renderOptionColor(value),
              /* @__PURE__ */ u("span", { class: "variant__radio-detail variant__radio-title", children: singleOptionSet ? value.title : value.label }),
              showVariantPrice ? /* @__PURE__ */ u("span", { class: "variant__radio-detail variant__radio-price", children: renderPriceString({
                singleOptionSet,
                value,
                optionLabel,
                currentOptionIndex
              }) }) : null
            ] })
          }
        ) }))
      }
    )
  ] });
};
const VariantSelectOption = ({
  option,
  dropdownOptions,
  defaultOption,
  currentOptionIndex,
  showOptionLabel,
  type,
  onSelect
}) => {
  option.name;
  let optionLabel = option.name;
  return /* @__PURE__ */ u("div", { class: `variant-wrap--select --${type || ""} z-10`, children: [
    showOptionLabel ? /* @__PURE__ */ u("p", { class: "variant-heading", children: optionLabel }) : null,
    /* @__PURE__ */ u("div", { class: "w-full pr-15", children: dropdownOptions.length > 1 ? /* @__PURE__ */ u(
      CustomSelect,
      {
        title: option.name,
        options: dropdownOptions,
        default: defaultOption,
        direction: "down",
        onSelected: ({ value }) => {
          console.log("Selected value", value);
          onSelect(
            currentOptionIndex,
            value && value.value ? value.value : value
          );
        }
      }
    ) : /* @__PURE__ */ u("span", { class: "text-small inline-block pl-10", children: dropdownOptions[0].label }) })
  ] });
};
const SvgCheckboxX = (props) => /* @__PURE__ */ _("svg", { xmlns: "http://www.w3.org/2000/svg", width: 9, height: 9, viewBox: "0 0 9 9", fill: "none", ...props }, /* @__PURE__ */ _("path", { d: "M8.46935 0L8.68109 0.211733L0.211756 8.68106L2.27206e-05 8.46933L8.46935 0Z", fill: "currentColor" }), /* @__PURE__ */ _("path", { d: "M8.68106 8.68106L8.46933 8.89279L0 0.423462L0.211734 0.211728L8.68106 8.68106Z", fill: "currentColor" }));
const VariantCheckboxOption = ({
  option,
  dropdownOptions,
  defaultOption,
  // We need to use this prop which contains the default selection
  currentOptionIndex,
  singleOptionSet,
  selectedOptions,
  variantData,
  productOptions,
  showOptionLabel,
  showVariantPrice,
  onSelect
}) => {
  let optionName = option.name;
  let optionLabel = option.name;
  let defaultOptionSet = dropdownOptions;
  if (singleOptionSet) {
    optionName = productOptions[0].name;
    defaultOptionSet = variantData;
  }
  const renderOptionColor = (value) => {
    return value.color ? /* @__PURE__ */ u(
      "span",
      {
        class: `variant__color ${value.color && luma(value.color) > 190 ? "light" : ""}`,
        style: { background: value.color }
      }
    ) : null;
  };
  return /* @__PURE__ */ u("div", { class: "variant-wrap variant-wrap--buttons", children: [
    showOptionLabel ? /* @__PURE__ */ u("span", { class: "variant-heading", children: optionLabel }) : null,
    /* @__PURE__ */ u(
      "div",
      {
        class: `variant__checkboxes variant__checkboxes--${handleize(optionName)}`,
        children: defaultOptionSet.map((value) => /* @__PURE__ */ u("div", { class: "variant__checkbox", children: /* @__PURE__ */ u(
          "button",
          {
            type: "button",
            class: `${[value.title, value.value, value.label].some(
              (val) => val === selectedOptions[currentOptionIndex]
            ) ? "active" : ""} `,
            onClick: () => onSelect(currentOptionIndex, value, singleOptionSet),
            "aria-label": `${optionName} ${value.label || value.title}`,
            children: /* @__PURE__ */ u("span", { class: "variant__checkbox-wrap", children: [
              /* @__PURE__ */ u("span", { class: "variant__checkbox-icon", children: /* @__PURE__ */ u(SvgCheckboxX, {}) }),
              renderOptionColor(value),
              /* @__PURE__ */ u("span", { class: "variant__checkbox-detail variant__checkbox-title", children: [
                singleOptionSet ? value.title : value.label,
                " "
              ] }),
              showVariantPrice ? /* @__PURE__ */ u("span", { class: "variant__checkbox-detail variant__checkbox-price", children: productFormUtils.renderPriceString({
                selectedOptions,
                variantData,
                optionLabel: value.label,
                singleOptionSet,
                value,
                currentOptionIndex,
                split: false,
                showCurrencyIso: true
              }) }) : null
            ] })
          }
        ) }))
      }
    )
  ] });
};
class VariantSelects extends x {
  constructor(props) {
    super(props);
    this.setPrimarySelectedOption = () => {
      const primaryOption = this.props.selectedVariant.options[0];
      this.setState({
        primarySelectedOption: primaryOption.value ? primaryOption.value : primaryOption
      });
    };
    this.updateURL = () => {
      if (!this.state.selectedVariant || !this.props.updateUrl) return;
      window.history.replaceState(
        {},
        "",
        `${this.props.url}?variant=${this.state.selectedVariant.id}`
      );
    };
    this.handleUpdate = () => {
      this.updateSelectedVariant();
      this.updateURL();
    };
    this.updateSelectedVariant = () => {
      try {
        let newVariant = this.props.variantData.find((variant) => {
          const matchResults = variant.options.map((option, index) => {
            const matches = this.state.selectedOptions[index] === option || this.state.selectedOptions[index] === option.value || (this.state.selectedOptions[index].value ? this.state.selectedOptions[index].value === option.value : false);
            return matches;
          });
          const result = !matchResults.includes(false);
          return result;
        });
        if (newVariant == void 0) {
          newVariant = this.props.variantData.find((variant) => {
            const firstOptionMatch = variant.options[0].value == this.state.selectedOptions[0].value || variant.options[0] == this.state.selectedOptions[0];
            return firstOptionMatch;
          });
        }
        this.props.updateVariant(newVariant);
      } catch (error) {
        console.error("Error in updateSelectedVariant:", error);
      }
    };
    this.updateSelectedOption = (option, value) => {
      const updatedOptions = [...this.state.selectedOptions];
      updatedOptions[option] = value.value;
      this.setState({
        selectedOptions: updatedOptions
      });
    };
    this.state = {
      selectedVariant: this.props.selectedVariant,
      selectedOptions: this.fixIntialOptionsFormat(
        this.props.initialVariant.options
      ),
      primarySelectedOption: this.props.initialVariant.options ? this.props.initialVariant.options[0] : this.props.initialVariant.selectedOptions ? this.props.initialVariant.selectedOptions[0].value : null
    };
    this.markup = null;
    this.currencyIso = window.currencyIso ? window.currencyIso : "";
    this.currencySymbol = window.currencySymbol ? window.currencySymbol : "$";
    this.showOptionLabel = this.props.showOptionLabel ? this.props.showOptionLabel : true;
    this.showVariantPrice = true;
  }
  componentDidUpdate(previousProps, previousState, snapshot) {
    if (JSON.stringify(this.state.selectedOptions) != JSON.stringify(previousState.selectedOptions)) {
      this.handleUpdate();
    }
  }
  fixIntialOptionsFormat(options) {
    if (options.length == 1) {
      return options[0].value ? options[0].value : options;
    }
    return options.map((option) => option.value ? option.value : option);
  }
  // Format Data Functions
  addColourData(formattedOptions) {
    const uniqueColors = Array.from(
      new Set(this.props.colorOptions.map((color) => color.name))
    ).map(
      (name) => this.props.colorOptions.find((color) => color.name === name)
    );
    return formattedOptions.map((option) => {
      const optionColor = uniqueColors.find(
        (color) => color.name === option.label
      );
      return {
        ...option,
        color: optionColor ? optionColor.hex : null,
        image: optionColor ? optionColor.image : null
      };
    });
  }
  filterAvailableOptionsBasedOnPrimaryOption(currentOptionIndex) {
    const primaryOption = this.state.primarySelectedOption;
    const filteredVariants = this.props.variantData.filter((variant) => {
      const variantOption = variant.options[0];
      return variantOption === primaryOption || variantOption.value === primaryOption.value || variantOption.value === primaryOption;
    });
    const uniqueMap = /* @__PURE__ */ new Map();
    filteredVariants.forEach((variant) => {
      const option = variant.options[currentOptionIndex];
      const value = option.value || option;
      const label = option.value || option;
      const key = typeof value === "object" ? value.value : value;
      if (!uniqueMap.has(key) || !uniqueMap.get(key).available && variant.available) {
        uniqueMap.set(key, {
          label,
          value,
          available: variant.available,
          id: variant.id
        });
      }
    });
    return Array.from(uniqueMap.values());
  }
  formatPrimaryOptions(formattedOptions, currentOptionIndex) {
    return formattedOptions.map((option) => {
      const optionValue = option.value;
      const available = this.props.variantData.some((variant) => {
        const variantOption = variant.options[currentOptionIndex];
        const variantValue = variantOption.value || variantOption;
        return (variantValue === optionValue || variantValue === optionValue.value) && variant.available;
      });
      return { ...option, available };
    });
  }
  formatDropdownOptions(currentOptionIndex, option, dropdownOptions) {
    let formattedOptions = currentOptionIndex === 0 ? this.formatPrimaryOptions(dropdownOptions, currentOptionIndex) : this.filterAvailableOptionsBasedOnPrimaryOption(currentOptionIndex);
    const isColorOption = /colou?r/i.test(option.name);
    if (isColorOption && this.props.colorOptions.length) {
      formattedOptions = this.addColourData(formattedOptions);
    }
    return formattedOptions;
  }
  getVariantMeta(id) {
    return this.props.variantMeta.find(
      (variant) => variant.id == id || variant.options.includes(id)
    );
  }
  // Field Render Functions
  customCheckboxOption(options) {
    const {
      option,
      dropdownOptions,
      defaultOption,
      currentOptionIndex,
      singleOptionSet
    } = options;
    const handleSelect = (optionIndex, value, isSingleOptionSet) => {
      if (isSingleOptionSet) {
        this.setState({
          selectedVariant: value,
          selectedOptions: value.options
        });
      } else {
        this.updateSelectedOption(optionIndex, value);
      }
    };
    return /* @__PURE__ */ u(
      VariantCheckboxOption,
      {
        option,
        dropdownOptions,
        defaultOption,
        currentOptionIndex,
        singleOptionSet,
        selectedOptions: this.state.selectedOptions,
        variantData: this.props.variantData,
        productOptions: this.props.productOptions,
        showOptionLabel: this.showOptionLabel,
        showVariantPrice: this.showVariantPrice,
        onSelect: handleSelect
      }
    );
  }
  customButtonOption(options) {
    const {
      option,
      dropdownOptions,
      defaultOption,
      currentOptionIndex,
      singleOptionSet
    } = options;
    const handleSelect = (optionIndex, value, isSingleOptionSet) => {
      if (isSingleOptionSet) {
        this.setState({
          selectedVariant: value,
          selectedOptions: value.options
        });
      } else {
        this.updateSelectedOption(optionIndex, value);
      }
    };
    return /* @__PURE__ */ u(
      VariantButtonOption,
      {
        option,
        dropdownOptions,
        defaultOption,
        currentOptionIndex,
        singleOptionSet,
        selectedOptions: this.state.selectedOptions,
        selectedVariant: this.state.selectedVariant,
        variantData: this.props.variantData,
        productOptions: this.props.productOptions,
        showOptionLabel: this.showOptionLabel,
        showVariantPrice: this.showVariantPrice,
        onSelect: handleSelect
      }
    );
  }
  customSelectOption(option, dropdownOptions, defaultOption, currentOptionIndex) {
    const handleSelect = (index, value) => {
      const updatedOptions = this.state.selectedOptions.slice();
      updatedOptions[index] = value;
      this.setState({ selectedOptions: updatedOptions });
    };
    return /* @__PURE__ */ u(
      VariantSelectOption,
      {
        option,
        dropdownOptions,
        defaultOption,
        currentOptionIndex,
        showOptionLabel: this.showOptionLabel,
        type: this.props.type,
        onSelect: handleSelect
      }
    );
  }
  singleOptionLabel(variant) {
    var _a;
    if ((_a = variant.public_title) == null ? void 0 : _a.includes("$")) {
      return `${this.currencySymbol}${(variant.price / 100).toFixed(2)}`;
    }
    return `${variant.name}`;
  }
  renderOptionMarkup() {
    return this.props.productOptions.map((option, currentOptionIndex) => {
      let optionsArray = option.options;
      let dropdownOptions = this.formatDropdownOptions(
        currentOptionIndex,
        option,
        optionsArray
      );
      const selectedVariantOption = this.state.selectedVariant.options[currentOptionIndex];
      const defaultOption = dropdownOptions.find((option2) => {
        var _a;
        const optionValue = ((_a = option2.value) == null ? void 0 : _a.value) || option2.value;
        const variantValue = (selectedVariantOption == null ? void 0 : selectedVariantOption.value) || selectedVariantOption;
        return optionValue === variantValue;
      }) || dropdownOptions[0];
      if (!this.state.selectedOptions[currentOptionIndex]) {
        const updatedOptions = [...this.state.selectedOptions];
        updatedOptions[currentOptionIndex] = defaultOption.value;
        this.setState({ selectedOptions: updatedOptions });
      }
      const optionStyle = currentOptionIndex >= this.props.productOptions.length - 1 ? this.props.lastOptionStyle : this.props[`option${currentOptionIndex + 1}Style`];
      switch (optionStyle) {
        case "buttons":
          return this.customButtonOption({
            option,
            dropdownOptions,
            defaultOption,
            currentOptionIndex,
            singleOptionSet: this.props.productOptions.length == 1 && this.props.variantData.length > 1
          });
        case "checkboxes":
          return this.customCheckboxOption({
            option,
            dropdownOptions,
            defaultOption,
            currentOptionIndex,
            singleOptionSet: this.props.productOptions.length == 1 && this.props.variantData.length > 1
          });
        // default also handles selects
        default:
          return this.customSelectOption(
            option,
            dropdownOptions,
            defaultOption,
            currentOptionIndex
          );
      }
    });
  }
  render() {
    if (this.props.variantData.length == 1 && this.props.variantData[0].title == "Default Title") {
      return null;
    }
    return /* @__PURE__ */ u(k, { children: this.state.selectedVariant ? /* @__PURE__ */ u("div", { class: "variant-selectors", children: this.renderOptionMarkup() }) : null });
  }
}
const SvgPlus = (props) => /* @__PURE__ */ _("svg", { xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", focusable: "false", className: "icon icon-plus", fill: "none", viewBox: "0 0 10 10", ...props }, /* @__PURE__ */ _("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z", fill: "currentColor" }));
const SvgMinus = (props) => /* @__PURE__ */ _("svg", { xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", focusable: "false", className: "icon icon-minus", fill: "none", viewBox: "0 0 10 2", ...props }, /* @__PURE__ */ _("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z", fill: "currentColor" }));
export {
  SvgMinus as S,
  VariantSelects as V,
  SvgPlus as a
};
